<template>
	<div
		class="input-wrapper"
		:class="wrapperClasses"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
	>
		<input
			ref="inputElement"
			:class="{
				'style-input': true,
				'style-input--error': props.error
			}"
			:type="actualFieldType"
			:name="name"
			:value="localValue"
			@input="handleInput"
			@focus="onFocus"
			@blur="onBlur"
			@keydown.enter="onPressEnter"
			@keydown.esc="onPressEsc"
			:style="inputStyle"
			:placeholder="placeholder"
			:autocomplete="autocomplete"
		/>

		<label
			v-if="label"
			class="floating-label"
			:class="{ 'active': isFocused || hasValue }"
		>
			{{ label }}
			<span
				v-if="required"
				class="floating-label__required"
			>*</span>
		</label>

		<span
			v-if="showIcons"
			class="search_buttons_container"
		>
		<!-- Кнопка очистки поля -->
			<app-tooltip
				v-if="showClearIcon && showClearButton"
				:text="props.clearHintText"
			>
				<template #activator>
					<app-icon
						v-if="showClearIcon && showClearButton"
						:name="actualIconCloseName"
						width="16px"
						color="#979797"
						cursor="pointer"
						@click="clearField"
						@mousedown.prevent
					/>
				</template>
			</app-tooltip>

				<!-- Иконка валидации (успех) -->
			<app-tooltip
				v-if="showValidationIcon && isValid && !error && !isFocused && validationHintText"
				:text="validationHintText"
			>
				<template #activator>
					<img
						:src="actualIconCheckPath"
						width="16"
						height="16"
						class="pointer"
					/>
				</template>
			</app-tooltip>
			<img
				v-else-if="showValidationIcon && isValid && !error && !isFocused"
				:src="actualIconCheckPath"
				width="16"
				height="16"
				class="pointer"
			/>

				<!-- Иконка валидации (ошибка) -->
			<app-tooltip
				v-if="showValidationIcon && (!isValid || error) && !isFocused && errorHintText"
				:text="errorHintText"
			>
				<template #activator>
					<img
						:src="actualIconInvalidPath"
						width="16"
						height="16"
						class="pointer"
					/>
				</template>
			</app-tooltip>
			<img
				v-else-if="showValidationIcon && (!isValid || error) && !isFocused"
				:src="actualIconInvalidPath"
				width="16"
				height="16"
				class="pointer"
			/>

			<app-tooltip
				v-if="showPasswordToggle && type === 'password'"
				:text="isPasswordVisible ? hidePasswordHintText : showPasswordHintText"
			>
				<template #activator>
					<img
						:src="visibilityIcon"
						width="16"
						height="16"
						class="pointer"
						@click="togglePasswordVisibility"
					/>
				</template>
			</app-tooltip>

			<app-tooltip
				v-if="showAddButton && localValue.length"
				:text="addHintText"
			>
				<template #activator>
					<app-icon
						name="plus"
						width="16px"
						color="#979797"
						cursor="pointer"
						hover-color="#499259"
						@click="add"
						@mousedown.prevent
					/>
				</template>
			</app-tooltip>

			<!-- <img
				v-if="showAddButton"
				:src="iconAddPath"
				width="16"
				height="16"
				class="pointer"
				@mouseenter="showAddHint = true"
				@mouseleave="showAddHint = false"
				@click="add"
				@mousedown.prevent
			/>

			<div
				v-if="showAddHint && showAddButton && addHintText"
				class="hint-container"
			>
				<div class="hint-content">
					{{ addHintText }}
				</div>
			</div> -->

			<app-tooltip
				v-if="showSearchButton && localValue.length"
				:text="searchHintText"
			>
				<template #activator>
					<app-icon
						name="search-magnify"
						width="16px"
						color="#979797"
						cursor="pointer"
						hover-color="#499259"
						@click="search"
						@mousedown.prevent
					/>
				</template>
			</app-tooltip>

			<!-- <img
				v-if="showSearchButton"
				:src="iconSearchPath"
				width="16"
				height="16"
				class="pointer"
				@mouseenter="showSearchHint = true"
				@mouseleave="showSearchHint = false"
				@click="search"
				@mousedown.prevent
			/>

			<div
				v-if="showSearchHint && showSearchButton && searchHintText"
				class="hint-container"
			>
				<div class="hint-content">
					{{ searchHintText }}
				</div>
			</div> -->

			<!-- Слот для кастомных иконок -->
			<slot name="custom-icons"></slot>
		</span>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	onMounted,
	watch,
	onBeforeUnmount
} from 'vue';
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { debounce } from "lodash";

type AppInputProps = {
	modelValue: string,
	type?: string,
	name?: string,
	label?: string,
	placeholder?: string,
	autocomplete?: string,
	error?: boolean,
	focused?: boolean,
	// Очистка поля
	showClearButton?: boolean,
	clearHintText?: string,
	// Добавление
	showAddButton?: boolean,
	addHintText?: string,
	// Поиск
	showSearchButton?: boolean,
	searchHintText?: string,
	// Валидация
	validationRegex?: RegExp | string | null,
	showValidationIcon?: boolean,
	errorHintText?: string,
	validationHintText?: string,
	// Иконки
	iconCheckPath?: string,
	iconClearName?: string,
	iconInvalidPath?: string,
	iconVisiblePath?: string,
	iconUnvisiblePath?: string,
	// Отображение пароля
	showPasswordToggle?: boolean,
	showPasswordHintText?: string,
	hidePasswordHintText?: string,
	// Дополнительные опции
	disableAutofillDetection?: boolean,
	// Дебаунс эмита значения наверх (мс). 0 = синхронно
	// Локальное отображение символа всегда мгновенное, дебаунсятся только эмиты update:modelValue/input.
	debounce?: number,
	// Обязательное поле — рисует красную звёздочку у floating-label
	required?: boolean
}

const props = withDefaults(defineProps<AppInputProps>(), {
	type: 'text',
	name: '',
	label: '',
	placeholder: '',
	autocomplete: 'off',
	error: false,
	focused: false,
	showClearButton: true,
	clearHintText: 'Очистить поле',
	showAddButton: false,
	addHintText: 'Добавить',
	showSearchButton: false,
	searchHintText: 'Поиск',
	validationRegex: null,
	showValidationIcon: false,
	errorHintText: '',
	validationHintText: 'Поле заполнено верно',
	iconCheckPath: '',
	iconClearName: '',
	iconInvalidPath: '',
	iconVisiblePath: '',
	iconUnvisiblePath: '',
	showPasswordToggle: false,
	showPasswordHintText: 'Показать пароль',
	hidePasswordHintText: 'Скрыть пароль',
	disableAutofillDetection: false,
	debounce: 0,
	required: false
})

const emit = defineEmits([
	'update:modelValue',
	'input',
	'focus',
	'blur',
	'clear-error',
	'clear-field',
	'validation-change',
	'password-visibility-change',
	'add',
	'search',
	'pressEnter',
	'pressEsc'
])

// Пути к иконкам по умолчанию
const defaultIconCheckPath = new URL('@/assets/image/icon-check.svg', import.meta.url).href;
const defaultIconInvalidPath = new URL('@/assets/image/icon-invalid.svg', import.meta.url).href;
const defaultIconVisiblePath = new URL('@/assets/image/icon-visible.svg', import.meta.url).href;
const defaultIconUnvisiblePath = new URL('@/assets/image/icon-unvisible.svg', import.meta.url).href;
const iconAddPath = new URL('@/assets/image/icon-add.svg', import.meta.url).href;
const iconSearchPath = new URL('@/assets/image/icon-search.svg', import.meta.url).href;

// Вычисляемые пути к иконкам
const actualIconCloseName = computed(() =>
	props.iconClearName || 'arrow-left'
);

const actualIconCheckPath = computed(() =>
	props.iconCheckPath || defaultIconCheckPath
);
const actualIconInvalidPath = computed(() =>
	props.iconInvalidPath || defaultIconInvalidPath
);
const actualIconVisiblePath = computed(() =>
	props.iconVisiblePath || defaultIconVisiblePath
);
const actualIconUnvisiblePath = computed(() =>
	props.iconUnvisiblePath || defaultIconUnvisiblePath
);

// Реактивные состояния
const isFocused = ref(props.focused);
const showAddHint = ref(false);
const showSearchHint = ref(false);
const showClearIconMouseHold = ref(false);
const hasValue = ref(false);
const isPasswordVisible = ref(false);
const inputElement = ref<HTMLInputElement | null>(null);
const localValue = ref(props.modelValue ?? '');

// Вычисляемые свойства
const showIcons = computed(() => {
	return isFocused.value || hasValue.value || props.error;
});

const showClearIcon = computed(() => {
	return (isFocused.value || showClearIconMouseHold.value) && localValue.value.length > 0;
});

const compiledRegex = computed<RegExp | null>(() => {
	if (!props.validationRegex) return null;
	return props.validationRegex instanceof RegExp
		? props.validationRegex
		: new RegExp(props.validationRegex);
});

const isValid = computed(() => {
	if (!compiledRegex.value) return true;
	return compiledRegex.value.test(localValue.value);
});

const actualFieldType = computed(() => {
	if (props.type === 'password' && props.showPasswordToggle && isPasswordVisible.value) {
		return 'text';
	}
	return props.type;
});

const visibilityIcon = computed(() => {
	return isPasswordVisible.value ? actualIconUnvisiblePath.value : actualIconVisiblePath.value;
});

const wrapperClasses = computed(() => ({
	'focused': isFocused.value || hasValue.value,
	'has-error': props.error,
	'has-value': hasValue.value,
	'is-valid': isValid.value && !props.error,
	'password-field': props.type === 'password'
}));

const inputStyle = computed(() => {
	let visibleIconsCount = 0;

	// Кнопка очистки
	if (showClearIcon.value && props.showClearButton) visibleIconsCount++;

	// Иконки поиска и добавления
	if (props.showSearchButton) visibleIconsCount++;
	if (props.showAddButton) visibleIconsCount++;

	// Иконки валидации
	if (props.showValidationIcon && isValid.value && !props.error && !isFocused.value) visibleIconsCount++;
	if (props.showValidationIcon && (!isValid.value || props.error) && !isFocused.value) visibleIconsCount++;

	// Кнопка отображения пароля
	if (props.showPasswordToggle && props.type === 'password') visibleIconsCount++;

	const basePadding = 16;
	const iconWidth = 16;
	const iconGap = 4;
	const additionalPadding = visibleIconsCount * (iconWidth + iconGap);

	return {
		paddingRight: `${basePadding + additionalPadding}px`
	};
});

// Методы
const checkAutofill = () => {
	if (props.disableAutofillDetection) return;

	if (inputElement.value) {
		const isAutofilled =
			inputElement.value.matches(':-webkit-autofill') ||
			inputElement.value.matches(':autofill') ||
			inputElement.value.value.length > 0;

		hasValue.value = Boolean(isAutofilled || (props.modelValue && props.modelValue.length > 0));
	}
};

const emitValueNow = (value: string) => {
	emit('update:modelValue', value);
	emit('input', value);
	emit('validation-change', isValid.value);
};

let debouncedEmit = debounce(emitValueNow, props.debounce);
watch(() => props.debounce, (wait) => {
	debouncedEmit.cancel();
	debouncedEmit = debounce(emitValueNow, wait);
});

const emitValue = (value: string) => {
	if (props.debounce > 0) {
		debouncedEmit(value);
	} else {
		emitValueNow(value);
	}
};

const flushEmit = () => {
	if (props.debounce > 0) {
		debouncedEmit.flush();
	}
};

const cancelEmit = () => {
	if (props.debounce > 0) {
		debouncedEmit.cancel();
	}
};

const handleInput = (event: Event) => {
	const target = event.target as HTMLInputElement;
	localValue.value = target.value;
	hasValue.value = target.value.length > 0;
	emit('clear-error');
	emitValue(target.value);
};

const onFocus = () => {
	isFocused.value = true;
	emit('focus');
};

const onBlur = () => {
	setTimeout(() => {
		isFocused.value = false;
		emit('blur');
	}, 100);
};

const onPressEnter = () => {
	flushEmit();
	emit('pressEnter');
}

const onPressEsc = (event: KeyboardEvent) => {
	if (localValue.value) {
		event.preventDefault();
		event.stopPropagation();
		cancelEmit();
		localValue.value = '';
		hasValue.value = false;
		emit('pressEsc');
		emit('update:modelValue', '');
		emit('input', '');
		emit('clear-field');
		emit('clear-error');
		inputElement.value?.blur();
		return;
	}

	emit('pressEsc');
}

onBeforeUnmount(() => {
  debouncedEmit.cancel();
});

const clearField = (event: Event) => {
	event.preventDefault();

	cancelEmit();
	localValue.value = '';
	hasValue.value = false;
	emit('update:modelValue', '');
	emit('input', '');
	emit('clear-field');
	emit('clear-error');

	setTimeout(() => {
		if (inputElement.value) {
			isFocused.value = true;
			inputElement.value.focus();
		}
	}, 0);
};

const search = (event: Event) => {
	event.preventDefault();

	flushEmit();
	emit('search');

	setTimeout(() => {
		if (inputElement.value) {
			isFocused.value = true;
			inputElement.value.focus();
		}
	}, 0);
};

const add = (event: Event) => {
	event.preventDefault();

	flushEmit();
	emit('add');

	setTimeout(() => {
		if (inputElement.value) {
			isFocused.value = true;
			inputElement.value.focus();
		}
	}, 0);
};

const togglePasswordVisibility = () => {
	isPasswordVisible.value = !isPasswordVisible.value;
	emit('password-visibility-change', isPasswordVisible.value);
};

const handleMouseEnter = () => {
	showClearIconMouseHold.value = true;
};

const handleMouseLeave = () => {
	showClearIconMouseHold.value = false;
};

const focusInput = () => {
	inputElement.value?.focus()
}

watch(() => props.modelValue, (newValue) => {
	if ((newValue ?? '') !== localValue.value) {
		localValue.value = newValue ?? '';
	}

	hasValue.value = Boolean(newValue && newValue.length > 0);

	if (!newValue && props.error) {
		emit('clear-error');
	}
});

watch(() => props.focused, (newValue) => {
	isFocused.value = newValue;
});

watch(() => props.type, (newType) => {
	if (newType === 'password') {
		isPasswordVisible.value = false;
		// hidePasswordIcons();
	}
});

// Хуки жизненного цикла
onMounted(() => {
	setTimeout(() => {
		checkAutofill();
	}, 100);
});

defineExpose({ focusInput })
</script>

<style scoped lang="scss">
.input-wrapper {
	position: relative;
	display: flex;
	// width: max-content;
}

.input-wrapper:hover .style-input {
	border-color: #3F884F;
}

.style-input {
  &__file {
    border: none;
    border-bottom: 1px solid #D2D2D2;
    border-radius: 0px;
    background: transparent;
  }
}

/* Стили для полей пароля */
.password-wrapper .style-input {
	-webkit-text-security: disc;
}

.style-input {
	&--error {
		border-color: #FF5533 !important;
	}
}

/* Скрытие стандартных иконок пароля в разных браузерах */
.style-input[type="password"]::-ms-reveal,
.style-input[type="password"]::-ms-clear {
	display: none;
}

.style-input[type="password"]::-webkit-credentials-auto-fill-button,
.style-input[type="password"]::-webkit-caps-lock-indicator {
	display: none;
	visibility: hidden;
	pointer-events: none;
}

.floating-label {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9C9C9C;
	font-size: 14px;
	pointer-events: none;
	transition: all 0.3s ease;
	background: white;
	padding: 0 4px;
	font-family: 'Roboto', sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}

.floating-label.active {
	top: 0;
	font-size: 10px;
	color: #3F884F;
}

.floating-label__required {
	color: #FF5533;
	margin-left: 0.2em;
}

.search_buttons_container {
	position: absolute;
	top: 50%;
	right: 12px;
	transform: translateY(-50%);
	display: flex;
	gap: 8px;
	align-items: center;
}

.hint-container {
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%);
	margin-bottom: 8px;
	z-index: 1000;
}

.hint-content {
	background: #5D5D5D;
	opacity: 0.9;
	color: white;
	width: max-content;
	max-width: 200px;
	height: auto;
	padding: 4px 8px;
	border-radius: 8px;
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	line-height: 16px;
	backdrop-filter: blur(4px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
	white-space: normal;
	word-wrap: break-word;
}

.pointer {
	cursor: pointer;
	margin-top: 5px;
}

/* @media (max-width: 768px) {
  .style-input {
    width: 100% !important;
  }
} */
</style>
